import React, { useMemo, useState, VoidFunctionComponent } from "react";
import { ProductImageMobile } from "@/components/responsive/product/productImageGrid/productImageMobile/ProductImageMobile";
import { ImageSliderModal } from "@/components/responsive/product/imageSliderModal/ImageSliderModal";
import { ButtonBorder } from "@/components/responsive/commun/buttonBorder/ButtonBorder";
import { TagsInsideImage } from "@/components/responsive/product/productImageGrid/TagsInsideImage";
import { RenderImageThumbnail } from "@/components/responsive/product/renderImageThumbnail/RenderImageThumbnail";
import { useBodyOverflow } from "@/customHooks/hooks";
import useWindowSize from '@/utils/responsive/hooks/useWindowSize.js';
import { AdaptiveLoadingResources } from "@/models/scope/productSheet/AdaptiveLoadingResources";
import { CommercialOperations } from "@/models/scope/productSheet/CommercialOperations";
import { ProductFeature } from "@/models/scope/productSheet/ProductFeature";
import { ProductImages } from "@/models/scope/productSheet/ProductImages";
import { ProductPackItem } from "@/models/scope/productSheet/ProductPackItem";
import { Offers } from "@/models/scope/productSheet/offer/Offers";
import { ImageSizes } from "@/models/scope/productSheet/ProductImages";
import {
  VISUAL_MAX_IMAGES_TO_DISPLAY,
  FRONT_VUE_PRESET_REGEX,
  BUNDLE_TYPE_PRODUCT,
  AMBIANCE_VUE_PRESET_REGEX,
  POSTION_VIDEO_1,
  POSTION_VIDEO_2,
  BREAKPOINT_DESKTOP,
} from "@/utils/constants";
import { getProductImages } from "@/utils/responsive/utils";
import {ProductDocumentList} from "@/models/scope/productSheet/ProductDocumentList";

interface Props {
  alt: string;
  isMobile: boolean;
  videoUrl?: string;
  videoUrl2?: string;
  documentId: ProductDocumentList<string> | null,
  adaptiveLoadingResources: AdaptiveLoadingResources;
  type: string;
  packItems: ProductPackItem[];
  images: ProductImages;
  bundleImageCode: string;
  id: string;
  newEnergyClass: string|null;
  repairabilityIndex: string;
  commercialOperations: CommercialOperations[];
  soldBy: string;
  eligibilityToPrivateSale: boolean;
  offersData: Offers | null;
  features: ProductFeature[];
}

export const ProductImageGrid: VoidFunctionComponent<Props> = ({
  alt,
  isMobile,
  videoUrl,
  videoUrl2,
  adaptiveLoadingResources,
  type,
  documentId,
  packItems,
  images,
  bundleImageCode,
  id,
  newEnergyClass,
  repairabilityIndex,
  commercialOperations,
  soldBy,
  eligibilityToPrivateSale,
  offersData,
  features,
}) => {
  const [isSliderModalShow, setIsSliderModalShow] = useState(false);
  const [indexToShow, setIndexToShow] = useState(0);
  useBodyOverflow(isSliderModalShow);

  let zoomImages: string[] = getProductImages(type, images, ImageSizes.ZOOM, id, bundleImageCode);
  let mainImages: string[] = getProductImages(type, images, ImageSizes.XL, id, bundleImageCode, "p");
  let positionVideo1: number | undefined = undefined;
  let positionVideo2: number | undefined = undefined;
  if (typeof window !== "undefined") {
    isMobile = useWindowSize().width <= BREAKPOINT_DESKTOP;
  }
  if (videoUrl) {
    positionVideo1 = mainImages.length < POSTION_VIDEO_1 ? mainImages.length : POSTION_VIDEO_1 - 1;
    mainImages.splice(positionVideo1, 0, mainImages?.find((media: string) =>
      FRONT_VUE_PRESET_REGEX.test(media)) || mainImages[1]);
  }
  if (videoUrl2) {
    positionVideo2 = mainImages.length < POSTION_VIDEO_2 ? mainImages.length : POSTION_VIDEO_2 - 1;
    mainImages.splice(positionVideo2, 0, mainImages?.find((media: string) =>
      AMBIANCE_VUE_PRESET_REGEX.test(media)) || mainImages[0]);
  }
  if (type === BUNDLE_TYPE_PRODUCT) {
    const packItemsImages = packItems.map((item) => item.product.images.simpleImages).flat();
    const packItemsImagesZoom = packItemsImages.map((item) => item.replace('produit-xl', 'p-zoom'));
    mainImages = [
      ...mainImages,
      ...packItemsImages,
    ];
    zoomImages = [
      ...zoomImages,
      ...packItemsImagesZoom,
    ];
  }
  const mediasCount = mainImages?.length;
  const showMoreButton = mediasCount > VISUAL_MAX_IMAGES_TO_DISPLAY && !isMobile;
  const gridClass: string = useMemo(() => {
    let gridClassName = "multi";
    if (mediasCount === 1) {
      gridClassName = "mono";
    } else if (mediasCount === 2) {
      gridClassName = "duo";
    } else if (mediasCount <= 4) {
      gridClassName = "quad";
    }
    return gridClassName;
  }, [mediasCount]);

  // Define function to check if media contains video icon
  const isVideoIconDisplayed = (index: number): boolean => {
    return (!!videoUrl && positionVideo1 == index) || (!!videoUrl2 && positionVideo2 == index);
  };
  // Define function to handle opening video player
  const openVideoPlayer = (videoSliderIndex: number): void => {
    setIndexToShow(videoSliderIndex || 0);
    !isSliderModalShow && setIsSliderModalShow(true);
  };

  return (
    <>
      {isSliderModalShow &&
        <ImageSliderModal
          medias={mainImages}
          close={() => setIsSliderModalShow(false)}
          imageToShow={indexToShow}
          alt={alt}
          isMobile={isMobile}
          zoomImages={zoomImages}
          videoUrl={videoUrl}
          videoUrl2={videoUrl2}
          positionVideo1={positionVideo1}
          positionVideo2={positionVideo2}
          adaptiveLoadingResources={adaptiveLoadingResources}
          isVideoIconDisplayed={isVideoIconDisplayed}
        />
      }
      {isMobile ? (
        <ProductImageMobile
          medias={mainImages}
          setIsSliderModalShow={setIsSliderModalShow}
          isVideoIconDisplayed={isVideoIconDisplayed}
          alt={alt}
          openVideoPlayer={openVideoPlayer}
          videoUrl={videoUrl}
          positionVideo1={positionVideo1}
          setIndexToShow={setIndexToShow}
          commercialOperations={commercialOperations}
          eligibilityToPrivateSale={eligibilityToPrivateSale}
          soldBy={soldBy}
          newEnergyClass={newEnergyClass}
          documentId={documentId}
          offersData={offersData}
          repairabilityIndex={repairabilityIndex}
          features={features} />
      ) : (
        <div className={`product-image-grid ${gridClass}`}>
          {mainImages?.map((media: string, i: number) => {
            const isVideoIconShowen = isVideoIconDisplayed(i);
            return i < VISUAL_MAX_IMAGES_TO_DISPLAY && (
              <div key={`product-image-grid-${i}`}>
                <RenderImageThumbnail
                  key={i}
                  image={media}
                  alt={alt}
                  isLazy={false}
                  className={isVideoIconShowen ? "video-container" : ""}
                  onClick={() => {
                    setIndexToShow(i);
                    setIsSliderModalShow(true);
                  }}
                  isVideoIconDisplayed={isVideoIconShowen}
                  openVideoPlayer={() => openVideoPlayer(i)}>
                  {i === 0 ? (
                    <TagsInsideImage
                      commercialOperations={commercialOperations}
                      newEnergyClass={newEnergyClass}
                      documentId={documentId}
                      reparabilityIndex={repairabilityIndex}
                      elibiblitySoldBy={['BUT', 'BUT_MKP']}
                      eligibilityToPrivateSale={eligibilityToPrivateSale}
                      soldBy={soldBy}
                      offersData={offersData}
                      features={features} 
                    />
                  ) : <></>}
                </RenderImageThumbnail>
              </div>
            );
          })}
          {showMoreButton && (
            <div className="button-more">
              <ButtonBorder
                onClick={() => {
                  setIsSliderModalShow(true);
                }}
                label="Voir plus de photos"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};